"use client";

import React, {useEffect, useRef, useState} from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {FaRegBell} from "react-icons/fa6";
import {HiArrowLongLeft, HiArrowLongRight} from "react-icons/hi2";
import {IoCloseSharp} from "react-icons/io5";
import {MdNotificationsNone} from "react-icons/md";
import {NotificationView} from "@/components/notifications/NotificationView";
import {NotificationToasts} from "@/components/notifications/NotificationToasts";
import type {Locale} from "@/i18n-config";
import type {
    AccountNotification,
    AccountNotificationFeed,
} from "@/utils/notifications/account/types";
import {parseAccountNotifications} from "@/utils/notifications/account/types";
import {createClient} from "@/utils/supabase/client";
import {InlineText} from "@/components/content/common/InlineText";

const PAGE_SIZE = 10;
const PAGE_FETCH_SIZE = PAGE_SIZE + 1;
const REALTIME_REFRESH_DEBOUNCE_MS = 250;

export type NotificationDictionary = {
    notificationCenterTitle: string;
    notificationCenterDescription: string;
    notificationCenterOpen: string;
    notificationCenterClose: string;
    notificationCenterUnreadCount: string;
    notificationCenterMarkAllRead: string;
    notificationCenterMarkRead: string;
    notificationCenterPinned: string;
    notificationCenterPagination: string;
    notificationCenterPreviousPage: string;
    notificationCenterNextPage: string;
    notificationCenterPageStatus: string;
    notificationCenterShowMore: string;
    notificationCenterShowLess: string;
    notificationCenterEmptyTitle: string;
    notificationCenterEmptyDescription: string;
    notificationCenterUpdateError: string;
    notificationCenterUpdateRetry: string;
    notificationToastClose: string;
    notificationAccountVerifiedTitle: string;
    notificationAccountVerifiedDescription: string;
    notificationAccountVerifiedAction: string;
    notificationAccountCategory: string;
    notificationFeatureCategory: string;
    notificationExportCategory: string;
    notificationExportStartedTitle: string;
    notificationExportStartedDescription: string;
    notificationExportReadyTitle: string;
    notificationExportReportDescription: string;
    notificationExportReadyAction: string;
    notificationReleaseNoteLabel: string;
    notificationReleaseNoteAction: string;
};

function withReadAt(
    notifications: AccountNotification[],
    readAt: string,
    notificationId?: string,
) {
    return notifications.map((notification) => {
        if (
            notification.read_at
            || (notificationId && notification.id !== notificationId)
        ) {
            return notification;
        }
        return {...notification, read_at: readAt};
    });
}

export function NotificationDrawer({
    locale,
    accountId,
    dictionary,
    initialFeed,
    initialLoadError = false,
}: {
    locale: Locale;
    accountId: string;
    dictionary: NotificationDictionary;
    initialFeed?: AccountNotificationFeed;
    initialLoadError?: boolean;
}) {
    const feed = initialFeed ?? {notifications: [], unreadCount: 0, hasMore: false};
    const feedInitializedRef = useRef(Boolean(initialFeed));
    const [open, setOpen] = useState(false);
    const [notifications, setNotifications] = useState(feed.notifications);
    const [unreadCount, setUnreadCount] = useState(feed.unreadCount);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(feed.hasMore ?? false);
    const [pageLoading, setPageLoading] = useState(false);
    const [updateError, setUpdateError] = useState(initialLoadError);
    const [markingAllRead, setMarkingAllRead] = useState(false);
    const [toastNotifications, setToastNotifications] = useState<
        AccountNotification[]
    >([]);
    const knownNotificationIdsRef = useRef(
        new Set(feed.notifications.map(({id}) => id)),
    );
    const pageRef = useRef(1);
    const refreshFeedRef = useRef<(page?: number) => void>(() => undefined);

    useEffect(() => {
        const supabase = createClient();
        let active = true;
        let refreshTimer: ReturnType<typeof setTimeout> | undefined;
        let refreshInFlight = false;
        let refreshQueued = false;
        let announceNewNotifications = false;
        let pendingPage = 1;
        const channels: ReturnType<typeof supabase.channel>[] = [];
        const replaySince = Date.now();
        const topics = [
            "notifications:global",
            `notifications:account:${accountId}`,
        ];

        async function refreshFeed() {
            if (!active) {
                return;
            }
            if (refreshInFlight) {
                refreshQueued = true;
                return;
            }

            refreshInFlight = true;
            const shouldAnnounce = announceNewNotifications && feedInitializedRef.current;
            const requestedPage = shouldAnnounce ? 1 : pendingPage;
            announceNewNotifications = false;
            try {
                const [
                    {data, error},
                    {data: nextUnreadCount, error: unreadCountError},
                ] = await Promise.all([
                    supabase.rpc("get_account_notifications", {
                        max_results: PAGE_FETCH_SIZE,
                        result_offset: (requestedPage - 1) * PAGE_SIZE,
                    }),
                    supabase.rpc("get_account_notification_unread_count"),
                ]);

                if (error || unreadCountError) {
                    throw new Error(
                        error?.message
                        || unreadCountError?.message
                        || "Notification refresh failed.",
                    );
                }

                if (active) {
                    const fetchedNotifications = parseAccountNotifications(
                        data || [],
                    );
                    const nextNotifications = fetchedNotifications.slice(
                        0,
                        PAGE_SIZE,
                    );
                    const newNotifications = shouldAnnounce && requestedPage === 1
                        ? nextNotifications.filter(
                            ({id}) => !knownNotificationIdsRef.current.has(id),
                        )
                        : [];

                    fetchedNotifications.forEach(({id}) => {
                        knownNotificationIdsRef.current.add(id);
                    });
                    feedInitializedRef.current = true;
                    setNotifications(nextNotifications);
                    setHasMore(fetchedNotifications.length > PAGE_SIZE);
                    pageRef.current = requestedPage;
                    setPage(requestedPage);
                    setUnreadCount(Number(nextUnreadCount || 0));
                    setUpdateError(false);
                    if (newNotifications.length > 0) {
                        setToastNotifications((current) => [
                            ...current,
                            ...newNotifications.filter(
                                ({id}) => !current.some(
                                    (notification) => notification.id === id,
                                ),
                            ),
                        ]);
                    }
                }
            } catch (error) {
                announceNewNotifications ||= shouldAnnounce;
                console.error("Failed to refresh account notifications", error);
                if (active) {
                    setUpdateError(true);
                }
            } finally {
                refreshInFlight = false;
                if (active) {
                    setPageLoading(false);
                }
                if (active && refreshQueued) {
                    refreshQueued = false;
                    setPageLoading(true);
                    void refreshFeed();
                }
            }
        }

        function scheduleRefresh(
            shouldAnnounce = false,
            requestedPage = pageRef.current,
        ) {
            if (!active) {
                return;
            }
            announceNewNotifications ||= shouldAnnounce;
            pendingPage = shouldAnnounce ? 1 : requestedPage;
            if (refreshTimer) {
                clearTimeout(refreshTimer);
            }
            refreshTimer = setTimeout(() => {
                refreshTimer = undefined;
                void refreshFeed();
            }, REALTIME_REFRESH_DEBOUNCE_MS);
        }

        function refreshWhenVisible() {
            if (document.visibilityState === "visible") {
                scheduleRefresh(false, pageRef.current);
            }
        }

        refreshFeedRef.current = (requestedPage = pageRef.current) => {
            setPageLoading(true);
            scheduleRefresh(false, requestedPage);
        };

        async function subscribe() {
            try {
                await supabase.realtime.setAuth();
                if (!active) {
                    return;
                }

                for (const topic of topics) {
                    const channel = supabase
                        .channel(topic, {
                            config: {
                                private: true,
                                broadcast: {
                                    replay: {
                                        since: replaySince,
                                        limit: 1,
                                    },
                                },
                            },
                        })
                        .on(
                            "broadcast",
                            {event: "changed"},
                            () => {
                                setPageLoading(true);
                                scheduleRefresh(true, 1);
                            },
                        )
                        .subscribe((status, error) => {
                            if (!active) {
                                return;
                            }
                            if (status === "SUBSCRIBED") {
                                // Close the gap between the server-rendered feed and
                                // the moment the private channel becomes ready.
                                setPageLoading(true);
                                scheduleRefresh(true, 1);
                            } else if (
                                status === "CHANNEL_ERROR"
                                || status === "TIMED_OUT"
                                || status === "CLOSED"
                            ) {
                                console.error(
                                    `Notification channel ${topic} changed to ${status}`,
                                    error,
                                );
                                setUpdateError(true);
                            }
                        });
                    channels.push(channel);
                }
            } catch (error) {
                console.error("Failed to subscribe to account notifications", error);
                if (active) {
                    setUpdateError(true);
                }
            }
        }

        window.addEventListener("online", refreshWhenVisible);
        window.addEventListener("focus", refreshWhenVisible);
        document.addEventListener("visibilitychange", refreshWhenVisible);
        if (!feedInitializedRef.current) void refreshFeed();
        void subscribe();

        return () => {
            active = false;
            refreshFeedRef.current = () => undefined;
            if (refreshTimer) {
                clearTimeout(refreshTimer);
            }
            window.removeEventListener("online", refreshWhenVisible);
            window.removeEventListener("focus", refreshWhenVisible);
            document.removeEventListener("visibilitychange", refreshWhenVisible);
            for (const channel of channels) {
                void supabase.removeChannel(channel);
            }
        };
    }, [accountId]);

    async function markRead(notificationId: string) {
        const current = notifications.find(
            (notification) => notification.id === notificationId,
        );
        if (!current || current.read_at) {
            return;
        }

        const readAt = new Date().toISOString();
        setNotifications((value) => withReadAt(value, readAt, notificationId));
        setUnreadCount((value) => Math.max(0, value - 1));
        setUpdateError(false);

        try {
            const response = await fetch(
                `/api/notifications/account/${notificationId}/read`,
                {
                    method: "POST",
                    credentials: "same-origin",
                    keepalive: true,
                },
            );
            if (!response.ok) {
                throw new Error("Notification could not be marked as read.");
            }
        } catch {
            setNotifications((value) => value.map((notification) =>
                notification.id === notificationId
                    ? {...notification, read_at: null}
                    : notification
            ));
            setUnreadCount((value) => value + 1);
            setUpdateError(true);
        }
    }

    async function markAllRead() {
        if (unreadCount === 0 || markingAllRead) {
            return;
        }

        const previousNotifications = notifications;
        const previousUnreadCount = unreadCount;
        const readAt = new Date().toISOString();
        setMarkingAllRead(true);
        setNotifications((value) => withReadAt(value, readAt));
        setUnreadCount(0);
        setUpdateError(false);

        try {
            const response = await fetch("/api/notifications/account/read-all", {
                method: "POST",
                credentials: "same-origin",
            });
            if (!response.ok) {
                throw new Error("Notifications could not be marked as read.");
            }
        } catch {
            setNotifications(previousNotifications);
            setUnreadCount(previousUnreadCount);
            setUpdateError(true);
        } finally {
            setMarkingAllRead(false);
        }
    }

    const openLabel = dictionary.notificationCenterOpen.replace(
        "{count}",
        String(unreadCount),
    );

    return (
        <NotificationToasts
            notifications={toastNotifications}
            locale={locale}
            dictionary={dictionary}
            onDismiss={(notificationId) => {
                setToastNotifications((current) => current.filter(
                    ({id}) => id !== notificationId,
                ));
            }}
        >
            <Dialog.Root
                open={open}
                onOpenChange={(nextOpen) => {
                    setOpen(nextOpen);
                    if (nextOpen) {
                        refreshFeedRef.current(1);
                    }
                }}
            >
                <Dialog.Trigger asChild>
                    <button
                        type="button"
                        aria-label={openLabel}
                        title={openLabel}
                        className="relative inline-flex size-10 items-center justify-center rounded-full text-white outline outline-offset-2 outline-white transition-all duration-300 hover:outline-white/50 focus-visible:outline-white"
                    >
                        <FaRegBell
                            className="size-5 lg:size-6"
                            aria-hidden="true"
                        />
                        {unreadCount > 0 && (
                            <span
                                className="absolute -bottom-1 -right-1 flex size-5 items-center justify-center rounded-full bg-rose-600/90 text-[10px] font-bold leading-none text-white shadow-sm ring-2 ring-grass-600"
                                aria-hidden="true"
                            >
                                {unreadCount > 9 ? "9+" : unreadCount}
                            </span>
                        )}
                    </button>
                </Dialog.Trigger>

                <Dialog.Portal>
                    <Dialog.Overlay className="fixed inset-0 z-1002 bg-black/35 backdrop-blur-[2px] data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
                    <Dialog.Content className="fixed inset-y-0 right-0 z-1003 flex w-full max-w-md flex-col bg-white shadow-2xl outline-hidden data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:inset-y-2 sm:right-2 sm:rounded-xl">
                        <header className="flex items-start justify-between gap-4 border-b border-zinc-200 px-5 py-5">
                            <div>
                                <Dialog.Title className="text-xl font-semibold text-zinc-900">
                                    {dictionary.notificationCenterTitle}
                                </Dialog.Title>
                                <Dialog.Description className="mt-1 text-sm text-zinc-500">
                                    {unreadCount > 0
                                        ? dictionary.notificationCenterUnreadCount.replace(
                                            "{count}",
                                            String(unreadCount),
                                        )
                                        : dictionary.notificationCenterDescription}
                                </Dialog.Description>
                            </div>
                            <Dialog.Close asChild>
                                <button
                                    type="button"
                                    aria-label={dictionary.notificationCenterClose}
                                    className="flex size-10 shrink-0 items-center justify-center rounded-full text-zinc-600 transition-colors hover:bg-zinc-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grass-500"
                                >
                                    <IoCloseSharp
                                        className="size-6"
                                        aria-hidden="true"
                                    />
                                </button>
                            </Dialog.Close>
                        </header>

                        {unreadCount > 0 && (
                            <div className="flex justify-end border-b border-zinc-200 px-4 py-2">
                                <button
                                    type="button"
                                    disabled={markingAllRead}
                                    onClick={markAllRead}
                                    className="rounded-md px-2 py-1 text-sm font-semibold text-grass-700 transition-colors hover:bg-grass-500/10 disabled:cursor-wait disabled:opacity-60"
                                >
                                    {dictionary.notificationCenterMarkAllRead}
                                </button>
                            </div>
                        )}

                        {updateError && (
                            <p
                                role="alert"
                                className="m-4 rounded-md bg-rose-700/10 p-3 text-sm text-rose-700"
                            >
                                {dictionary.notificationCenterUpdateError}
                                {" "}
                                <button
                                    type="button"
                                    disabled={pageLoading}
                                    onClick={() => refreshFeedRef.current(page)}
                                    className="font-semibold underline underline-offset-2 disabled:cursor-wait disabled:opacity-60"
                                >
                                    {dictionary.notificationCenterUpdateRetry}
                                </button>
                            </p>
                        )}

                        <div
                            className="min-h-0 flex-1 overflow-y-auto overscroll-contain"
                            aria-busy={pageLoading}
                        >
                            {notifications.length > 0 ? (
                                notifications.map((notification) => (
                                    <NotificationView
                                        key={notification.id}
                                        notification={notification}
                                        locale={locale}
                                        dictionary={dictionary}
                                        onMarkRead={markRead}
                                        onInternalNavigate={() => setOpen(false)}
                                    />
                                ))
                            ) : (
                                <div className="flex h-full min-h-80 flex-col items-center justify-center px-8 text-center">
                                    <div className="flex size-14 items-center justify-center rounded-full bg-zinc-100 text-zinc-500">
                                        <MdNotificationsNone
                                            className="size-7"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <h3 className="mt-4 text-lg font-semibold text-zinc-900">
                                        {dictionary.notificationCenterEmptyTitle}
                                    </h3>
                                    <p className="mt-1 max-w-xs text-sm leading-relaxed text-zinc-500">
                                        <InlineText value={dictionary.notificationCenterEmptyDescription} />
                                    </p>
                                </div>
                            )}
                        </div>
                        {(page > 1 || hasMore) && (
                            <nav
                                aria-label={dictionary.notificationCenterPagination}
                                className="flex shrink-0 items-center justify-between border-t border-zinc-200 bg-white px-4 py-3"
                            >
                                <button
                                    type="button"
                                    disabled={page === 1 || pageLoading}
                                    onClick={() => refreshFeedRef.current(page - 1)}
                                    className="inline-flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-semibold text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900 disabled:pointer-events-none disabled:opacity-40"
                                >
                                    <HiArrowLongLeft
                                        className="size-5"
                                        aria-hidden="true"
                                    />
                                    {dictionary.notificationCenterPreviousPage}
                                </button>
                                <span className="text-sm text-zinc-500">
                                    {dictionary.notificationCenterPageStatus.replace(
                                        "{page}",
                                        String(page),
                                    )}
                                </span>
                                <button
                                    type="button"
                                    disabled={!hasMore || pageLoading}
                                    onClick={() => refreshFeedRef.current(page + 1)}
                                    className="inline-flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-semibold text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900 disabled:pointer-events-none disabled:opacity-40"
                                >
                                    {dictionary.notificationCenterNextPage}
                                    <HiArrowLongRight
                                        className="size-5"
                                        aria-hidden="true"
                                    />
                                </button>
                            </nav>
                        )}
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>
        </NotificationToasts>
    );
}
